import { TreeSelectItem } from '../components/common/TreeSelect';

/**
 * Transforms flat location or category data into a hierarchical tree structure
 * @param items Array of items with id, name, and parent_location or parent_category or parent_asset properties
 * @returns Array of TreeSelectItem with hierarchical structure
 */
export const transformToTreeStructure = <T extends { id: string; name: string; parent_location?: string | null; parent_category?: string | null; parent_asset?: string | null }>(
  items: T[]
): TreeSelectItem[] => {
  // Create a map of all items
  const itemMap: Record<string, TreeSelectItem> = {};
  
  // Initialize all items with empty children arrays
  items.forEach((item) => {
    itemMap[item.id] = { 
      id: item.id, 
      name: item.name,
      children: [],
      parent_id: item.parent_location || item.parent_category || item.parent_asset || undefined
    };
  });
  
  // Build the tree structure
  const rootItems: TreeSelectItem[] = [];
  
  // Add children to their parents
  Object.values(itemMap).forEach((item) => {
    if (item.parent_id && itemMap[item.parent_id]) {
      // This is a child item, add it to its parent's children
      if (!itemMap[item.parent_id].children) {
        itemMap[item.parent_id].children = [];
      }
      itemMap[item.parent_id].children!.push(item);
    } else {
      // This is a root item
      rootItems.push(item);
    }
  });
  
  return rootItems;
};

/**
 * Finds an item in a tree structure by its ID
 * @param items Array of TreeSelectItem
 * @param id ID to find
 * @returns The found item or undefined
 */
export const findItemById = (
  items: TreeSelectItem[],
  id: string
): TreeSelectItem | undefined => {
  for (const item of items) {
    if (item.id === id) {
      return item;
    }
    
    if (item.children && item.children.length > 0) {
      const foundInChildren = findItemById(item.children, id);
      if (foundInChildren) {
        return foundInChildren;
      }
    }
  }
  
  return undefined;
};